import React, { useEffect, useState } from "react";
import {
  Box,
  TextField,
  Button,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Checkbox,
  ListItemText,
  Snackbar,
  Alert,
  IconButton,
} from "@mui/material";
import {
  fetchPromotions,
  editPromotion,
  fetchCustomers,
  createAndSendPromotion,
} from "./components/smsService"; // Import createAndSendPromotion
import { useNavigate } from "react-router-dom";
import { DataGrid } from "@mui/x-data-grid";
import { useTheme } from "@mui/material";
import { tokens } from "../../../theme";

const PromotionsPage = () => {
  const theme = useTheme();
  const [message, setMessage] = useState("");
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessageText, setSuccessMessageText] = useState("");
  const [pastPromotions, setPastPromotions] = useState([]);
  const [selectedPromotion, setSelectedPromotion] = useState("");
  const navigate = useNavigate();
  const colors = tokens(theme.palette.mode);

  const decodedToken = JSON.parse(localStorage.getItem("decodedToken"));
  const userId = parseInt(decodedToken.Id);
  useEffect(() => {
    const loadPromotionsAndCustomers = async () => {
      try {
        const [promotions, customerList] = await Promise.all([
          fetchPromotions(),
          fetchCustomers(),
        ]);
        setPastPromotions(promotions);
        setCustomers(customerList);
      } catch (error) {
        console.error("Error loading promotions or customers:", error);
      }
    };

    loadPromotionsAndCustomers();
  }, []);

  const handleSendPromotion = async () => {
    if (message && (isAllSelected || selectedCustomers.length > 0)) {
      try {
        const isAllSelected = selectedCustomers.length === customers.length;


        const functionFlag = selectedCustomers.length > 0 ? (isAllSelected ? 1 : 0) : null;
        const customerIds = isAllSelected
          ? null
          : selectedCustomers
              .map((customerName) => {
                const customer = customers.find((c) => c.customerName === customerName);
                return customer ? customer.id : null; // Use `customer.id` instead of `customer.customerId`
              })
              .filter(Boolean); // Filter out any null values
  
        const promotionInput = {
          message,
          functionFlag,
          customerIds,
          createdBy: userId,
        };
  
  
        await createAndSendPromotion(promotionInput);
        setShowSuccessMessage(true);
        setSuccessMessageText("Promotion sent successfully!");
  
        // Reload promotions and reset fields
        const promotions = await fetchPromotions();
        setPastPromotions(promotions);
        setSelectedPromotion("");
        setMessage("");
        setSelectedCustomers([]);
      } catch (error) {
        console.error("Error sending promotion:", error);
        alert("Failed to send promotion. Please try again.");
      }
    } else {
      alert("Please enter a message and select customers to send to.");
    }
  };
  
  
  
  const handleCustomerChange = (event) => {
    const value = event.target.value;

    if (value.includes("all")) {
      // Toggle Select All
      if (isAllSelected) {
        setSelectedCustomers([]); // Deselect all
      } else {
        setSelectedCustomers(
          customers.map((customer) => customer.customerName)
        ); // Select all
      }
    } else {
      setSelectedCustomers(value);
    }
  };

  const isAllSelected = selectedCustomers.length === customers.length;

  const handleCloseSnackbar = () => {
    setShowSuccessMessage(false);
  };

  const handlePromotionSelect = (id) => {
    const selectedPromo = pastPromotions.find((promo) => promo.id === id);

    setSelectedPromotion(id);
    if (selectedPromo) {
      setMessage(selectedPromo.message);
    }
  };

  const handleEditPromotion = async () => {
    const promotionToEdit = {
      id: selectedPromotion,
      message,
      isActive: true,
      companyId: "yourCompanyId",
      createdDate: new Date().toISOString(),
      createdBy: "currentUserId",
      updatedDate: new Date().toISOString(),
      updatedBy: "currentUserId",
    };

    try {
      await editPromotion(promotionToEdit);
      setShowSuccessMessage(true);
      setSuccessMessageText("Promotion edited successfully!");
      const promotions = await fetchPromotions();
      setPastPromotions(promotions);
      setSelectedPromotion("");
      setMessage("");
    } catch (error) {
      console.error("Error editing promotion:", error);
      alert("Failed to edit promotion. Please try again.");
    }
  };

  const columns = [
    { field: "id", headerName: "ID", width: 100 },
    { field: "message", headerName: "Message", flex: 1 },
    {
      field: "createdDate",
      headerName: "Created Date",
      width: 150,
      valueGetter: (params) =>
        new Date(params.row.createdDate).toLocaleDateString(),
    },
    {
      field: "action",
      headerName: "Action",
      width: 150,
      renderCell: (params) => (
        <Button onClick={() => handlePromotionSelect(params.row.id)}>
          Select
        </Button>
      ),
    },
  ];

  return (
    <div style={{ padding: "20px" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "20px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center" }}>
    
          <h2>Create a New Promotion</h2>
        </div>
      </div>

      <FormControl fullWidth style={{ marginBottom: "20px" }}>
        <TextField
          label="Promotion Message"
          variant="outlined"
          multiline
          rows={4}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
        />
      </FormControl>

      <FormControl fullWidth style={{ marginBottom: "20px" }}>
        <InputLabel>Send to Customers</InputLabel>
        <Select
          multiple
          value={selectedCustomers}
          onChange={handleCustomerChange}
          renderValue={(selected) =>
            selected.length ? selected.join(", ") : "Select Customers"
          }
        >
          <MenuItem value="all">
            <Checkbox checked={isAllSelected} />
            <ListItemText primary="Select All" />
          </MenuItem>

          {customers.map((customer) => (
            <MenuItem key={customer.customerId} value={customer.customerName}>
              <Checkbox
                checked={selectedCustomers.includes(customer.customerName)}
              />
              <ListItemText primary={customer.customerName} />
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Button
        variant="contained"
        color="primary"
        onClick={handleSendPromotion}
        disabled={!message || !selectedCustomers.length}
      >
        Send Promotion
      </Button>

      <Button
        variant="contained"
        color="primary"
        onClick={handleEditPromotion}
        disabled={!selectedPromotion || !message}
        style={{ marginLeft: "10px" }}
      >
        Edit Promotion
      </Button>

      <Box
        m="40px 0 0 0"
        height="75vh"
        sx={{
          "& .MuiDataGrid-root": {
            border: "none",
          },
          "& .MuiDataGrid-cell": {
            borderBottom: "none",
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: colors.blueAccent[700],
            borderBottom: "none",
          },
          "& .MuiDataGrid-virtualScroller": {
            backgroundColor: colors.primary[400],
          },
          "& .MuiDataGrid-footerContainer": {
            borderTop: "none",
            backgroundColor: colors.blueAccent[700],
          },
        }}
      >
        <DataGrid
          rows={pastPromotions}
          columns={columns}
          pageSize={10}
          rowsPerPageOptions={[10]}
          disableSelectionOnClick
        />
      </Box>

      <Snackbar
        open={showSuccessMessage}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity="success"
          sx={{ width: "100%" }}
        >
          {successMessageText}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default PromotionsPage;
